import { NgClass, AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterOutlet } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { asapScheduler, BehaviorSubject, tap, combineLatest } from 'rxjs';

import { Scopes } from '@ninety/platform/models/entitlements/entitlements-model';
import {
  SpinnerService,
  FeatureFlagKeys,
  selectFeatureFlag,
  selectFeatureEnabled,
  MainLayoutIntercomActions,
  selectIsV2DetailRoute,
  selectLanguage,
} from '@ninety/platform/state';
import { TerraIconModule } from '@ninety/terra';
import { SideDetailExpandedService } from '@ninety/ui/core';
import { DetailServiceSelectors } from '@ninety/web/pages/detail-view/_state/detail-view.selectors';
import { GuideSelectors } from '@ninety/web/pages/getting-started/guide/_state/guide.selectors';
import { GettingStartedGuideComponent } from '@ninety/web/pages/getting-started/guide/components/getting-started-guide/getting-started-guide.component';
import { MazActions } from '@ninety/web/pages/maz/_state/maz.actions';
import { MazSelectors } from '@ninety/web/pages/maz/_state/maz.selectors';

import { MeetingPrepPanelComponent } from '../meeting-prep-panel/meeting-prep-panel.component';
import { MeetingPrepPanelService } from '../meeting-prep-panel/meeting-prep-panel.service';

import { MainHeaderComponent } from './main-header/main-header.component';

interface MainLayoutState {
  open: boolean | null;
  detailsPanelOverlayEnabled: boolean | null;
  mazChatEnabled: boolean | null;
  isMazChatOpen: boolean | null;
  isMazChatExpanded: boolean | null;
  isMazSessionInProgress: string | null;
  canAccessMazCDQ: boolean | null;
  rnaMeetingPrepEnabled: boolean | null;
  sideDetailExpanded: boolean | null;
  webMazGlobalEntryEnabled: boolean | null;
}

@Component({
  selector: 'ninety-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss', './main-layout.terrafy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    NgClass,
    RouterOutlet,
    MatProgressSpinner,
    TerraIconModule,
    MainHeaderComponent,
    GettingStartedGuideComponent,
    MeetingPrepPanelComponent,
  ],
})
export class MainLayoutComponent implements OnInit {
  spinnerService = inject(SpinnerService);
  private store = inject(Store);
  private actions$ = inject(Actions);
  private destroyRef = inject(DestroyRef);
  private meetingPrepPanelService = inject(MeetingPrepPanelService);

  isMinimizing = false;
  private minimizeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /** Overflow property to use on the main content wrappers. */
  protected overflowType: 'hidden' | 'clip' | 'auto';

  @Input() inMeeting: boolean;
  @Input() inFeedback: boolean;
  @Input() isMobile: boolean;
  @Input() isConversation: boolean;

  language$ = this.store.select(selectLanguage);

  /**
   * TODO: Remove this selector and apply the v2 "detail-card-v2-wrp" styles directly to the detail-wrapper in main-layout.component
   * once all detail cards have been updated to the new style.
   */
  isV2Detail$ = combineLatest([
    this.store.select(selectIsV2DetailRoute),
    // eslint-disable-next-line @ngrx/avoid-combining-selectors
    this.store.select(MazSelectors.selectIsMazChatOpen),
    toObservable(this.meetingPrepPanelService.isVisible),
  ]).pipe(
    tap(([isV2Detail, isMazChatOpen, isMeetingPrepVisible]) => {
      this.store.dispatch(
        isV2Detail || isMazChatOpen || isMeetingPrepVisible
          ? MainLayoutIntercomActions.hideIntercom()
          : MainLayoutIntercomActions.showIntercom()
      );
    })
  );
  disableScrollbar$ = this.store.select(DetailServiceSelectors.selectDisableScrollbar);

  guideEnabled$ = this.store.select(GuideSelectors.selectIsGuideEnabled);
  showGettingStartedGuide$ = this.store.select(GuideSelectors.selectIsGuideOpened);

  detailsPanelOverlayEnabled$ = this.store.select(selectFeatureFlag(FeatureFlagKeys.detailsPanelOverlay));
  mazChatEnabled$ = this.store.select(selectFeatureFlag(FeatureFlagKeys.mazSeatBuilder));
  isMazChatOpen$ = this.store.select(MazSelectors.selectIsMazChatOpen);
  isMazChatExpanded$ = this.store.select(MazSelectors.selectIsMazChatExpanded);
  isMazSessionInProgress$ = this.store.select(MazSelectors.selectSessionId);
  webMazGlobalEntryEnabled$ = this.store.select(selectFeatureFlag(FeatureFlagKeys.webMazGlobalEntry));
  disableMazAnimation = false;

  canAccessMazCDQ$ = this.store.select(selectFeatureEnabled(Scopes.mazConversationalData));
  rnaMeetingPrepEnabled$ = this.store.select(selectFeatureFlag(FeatureFlagKeys.rnaMeetingPrep));
  sideDetailExpanded$ = toObservable(inject(SideDetailExpandedService).expanded);

  // Used by ScrollSpy directive to get access to the main windows scrollable content.
  @ViewChild('pageContent') pageContentRef: ElementRef;

  open$ = new BehaviorSubject<boolean>(false);

  ngOnInit(): void {
    // See https://traxion.atlassian.net/browse/DEV-8296 for why we only use `hidden` for Firefox
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    this.overflowType = isFirefox ? 'hidden' : 'auto';

    this.destroyRef.onDestroy(() => {
      if (this.minimizeTimeoutId !== null) clearTimeout(this.minimizeTimeoutId);
    });

    this.actions$.pipe(ofType(MazActions.minimizeMazChatDetail), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.isMinimizing) return;
      this.isMinimizing = true;
      this.minimizeTimeoutId = setTimeout(() => {
        this.minimizeTimeoutId = null;
        this.isMinimizing = false;
        this.store.dispatch(MazActions.hideMazChatDetail());
      }, 300);
    });

    this.isMazChatOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isMazChatOpen => {
      this.meetingPrepPanelService.setMazOpen(isMazChatOpen);
      if (isMazChatOpen) {
        this.meetingPrepPanelService.minimizeForMaz();
      } else {
        this.meetingPrepPanelService.restoreAfterMaz();
      }
    });
  }

  onActivateDetail() {
    // On page load, the subject will initialize to false and then to true in the same tick and it doesn't always get
    // picked up changeDetection. The asapScheduler is like an optimized setTimeout, that runs the process in the next
    // change detection cyle.
    asapScheduler.schedule(() => this.open$.next(true));
  }

  onDeactivateDetail() {
    // On page load, the subject can initialize to true on refresh with the detail open, then be closed by an effect in
    // the same tick and it doesn't always get picked up changeDetection.
    asapScheduler.schedule(() => this.open$.next(false));
  }

  cdqTabIsVisible(state: MainLayoutState): boolean {
    return !!(
      !state.webMazGlobalEntryEnabled &&
      state.canAccessMazCDQ &&
      state.isMazSessionInProgress === '' &&
      !state.isMazChatOpen
    );
  }

  toggleMazChat(isMazChatOpen: boolean): void {
    if (!isMazChatOpen) {
      this.disableMazAnimation = true;
      setTimeout(() => {
        this.disableMazAnimation = false;
      }, 200);
    }
    this.store.dispatch(MazActions.toggleMazChatDetail());
  }
}
